import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  ClipboardCheck,
  Calendar,
  User,
  MapPin,
  Building2,
  Radio,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  Edit,
  Mail,
  Download,
  Loader2,
  ChevronDown,
  ChevronUp,
  Camera,
  MessageSquare,
  ExternalLink,
  Shield,
  Target,
  AlertCircle
} from 'lucide-react';
import { api } from '@/utils/api';
import { AuditPhoto } from '@/components/AuditPhoto';

/* S4: Export PDF uses window.print() — hide chrome, keep the report content. */
const printStyles = `
@media print {
  body { background: white !important; }
  body * { visibility: hidden; }
  .print-area, .print-area * { visibility: visible; }
  /* External audit LOW-5: no absolute positioning — absolute+inset clips
     multi-page audits to one printed page. */
  .print-area { width: 100%; background: white !important; }
  .print-area * { color: black !important; border-color: #ccc !important; }
  .no-print { display: none !important; }
}
`;

interface AuditDetail {
  id: number;
  auditId: string;
  program: string;
  auditType: string;
  status: string;
  siteName: string;
  siteId: string;
  siteAddress: string;
  city: string;
  state: string;
  region: string;
  market: string;
  carrierName: string;
  contractorName: string;
  auditorName: string;
  auditorEmail: string;
  constructionSupervisor: string;
  startDate: string;
  completedAt: string | null;
  scorePercentage: number | null;
  passFailStatus: string | null;
  totalPoints: number;
  earnedPoints: number;
  overallComments: string;
  auditorSignature: string;
  supervisorSignature: string;
  responses: Record<string, any>;
  crewMembers: any[];
  template: {
    name: string;
    elements: any[];
  } | null;
  findings: {
    id: number;
    findingNumber: string;
    title: string;
    description: string;
    severity: string;
    status: string;
    elementName: string;
    questionText: string;
    correctiveAction: string;
    actionDueDate: string | null;
  }[];
}

// More professional status labels
const STATUS_LABELS: Record<string, string> = {
  'Pass': 'Satisfactory',
  'Conditional Pass': 'Needs Attention',
  'Fail': 'Action Required',
};

const STATUS_STYLES: Record<string, { bg: string; text: string; border: string }> = {
  'Pass': { bg: 'bg-green-900/30', text: 'text-green-400', border: 'border-green-600' },
  'Conditional Pass': { bg: 'bg-yellow-900/30', text: 'text-yellow-400', border: 'border-yellow-600' },
  'Fail': { bg: 'bg-red-900/30', text: 'text-red-400', border: 'border-red-600' },
};

const SEVERITY_STYLES: Record<string, { bg: string; text: string }> = {
  OBSERVATION: { bg: 'bg-blue-900/30', text: 'text-blue-400' },
  MINOR: { bg: 'bg-yellow-900/30', text: 'text-yellow-400' },
  MAJOR: { bg: 'bg-orange-900/30', text: 'text-orange-400' },
  CRITICAL: { bg: 'bg-red-900/30', text: 'text-red-400' },
  STOP_WORK: { bg: 'bg-red-900/50', text: 'text-red-300' },
};

export function AuditDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [audit, setAudit] = useState<AuditDetail | null>(null);
  const [loading, setLoading] = useState(true);
  // S4: finding status progression (safety roles only).
  const [findingStatusSaving, setFindingStatusSaving] = useState<number | null>(null);
  const [canManageFindings] = useState<boolean>(() => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || 'null');
      return ['ADMIN', 'SAFETY_MANAGER', 'CONSTRUCTION_MANAGER', 'PROJECT_MANAGER', 'CONSTRUCTION_SUPERVISOR', 'SUPER_ADMIN'].includes(user?.role);
    } catch { return false; }
  });
  const updateFindingStatus = async (findingId: number, status: string) => {
    setFindingStatusSaving(findingId);
    try {
      const me = (() => { try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; } })();
      const verifier = me ? [me.firstName, me.lastName].filter(Boolean).join(' ') || me.email : undefined;
      await api.put(`/safety-audits/findings/${findingId}`, {
        status,
        // External audit LOW-2: closure/waiver carries the verifying identity.
        ...(status === 'CLOSED' || status === 'WAIVED' ? { verifiedBy: verifier } : {}),
      });
      await loadAudit(String(id));
    } catch (error: any) {
      alert(error?.response?.data?.message || 'Could not update the finding status — try again.');
    } finally {
      setFindingStatusSaving(null);
    }
  };
  const [expandedElements, setExpandedElements] = useState<Set<string>>(new Set());
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [recipients, setRecipients] = useState('');
  const [emailStatus, setEmailStatus] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const sendReport = async () => {
    setEmailSending(true); setEmailStatus('');
    try { await api.post(`/safety-audits/${id}/email`, { recipients: recipients.split(/[,;\s]+/).filter(Boolean) }); setEmailStatus('Report sent.'); }
    catch (err: any) { setEmailStatus(err.response?.data?.message || 'Report was not sent. Please retry.'); }
    finally { setEmailSending(false); }
  };

  useEffect(() => {
    if (id) {
      loadAudit(id);
    }
  }, [id]);

  const loadAudit = async (auditId: string) => {
    try {
      const response = await api.get(`/safety-audits/${auditId}`);
      setAudit(response.data.data);
    } catch (error) {
      console.error('Error loading audit:', error);
      navigate('/safety-audits');
    } finally {
      setLoading(false);
    }
  };

  const toggleElement = (elementId: string) => {
    setExpandedElements(prev => {
      const newSet = new Set(prev);
      if (newSet.has(elementId)) {
        newSet.delete(elementId);
      } else {
        newSet.add(elementId);
      }
      return newSet;
    });
  };

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-emerald-400';
    if (score >= 80) return 'text-green-400';
    if (score >= 70) return 'text-yellow-400';
    if (score >= 60) return 'text-orange-400';
    return 'text-red-400';
  };

  const getResponseIcon = (response: string | boolean | null) => {
    if (response === 'Yes' || response === true) {
      return <CheckCircle className="h-5 w-5 text-green-400" />;
    }
    if (response === 'No' || response === false) {
      return <XCircle className="h-5 w-5 text-red-400" />;
    }
    if (response === 'N/A') {
      return <span className="text-amber-400 font-medium text-sm">N/A</span>;
    }
    return <span className="text-slate-500 text-sm">—</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
      </div>
    );
  }

  if (!audit) {
    return null;
  }

  const statusStyle = audit.passFailStatus ? STATUS_STYLES[audit.passFailStatus] : null;
  const statusLabel = audit.passFailStatus ? STATUS_LABELS[audit.passFailStatus] : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <style>{printStyles}</style>
      {/* Header */}
      <div className="no-print bg-slate-800/50 border-b border-slate-700">
        <div className="container mx-auto px-6 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-4">
              <Link
                to="/safety-audits"
                className="inline-flex items-center gap-2 text-slate-300 hover:text-white text-sm font-semibold"
                aria-label="Back to Safety Audits list"
              >
                <ArrowLeft className="h-5 w-5" />
                Back to Audits
              </Link>
              <span className="text-slate-600">|</span>
              <Link
                to="/dashboard"
                className="inline-flex items-center gap-2 text-slate-300 hover:text-white text-sm font-semibold"
                aria-label="Go to Dashboard"
              >
                <img src="/logo.svg" alt="" className="h-5 w-5" />
                Dashboard
              </Link>
              <div>
                <div className="flex items-center gap-3 flex-wrap">
                  <h1 className="text-xl font-bold text-white">{audit.auditId}</h1>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    audit.status === 'COMPLETED' ? 'bg-green-900/30 text-green-400' :
                    audit.status === 'IN_PROGRESS' ? 'bg-blue-900/30 text-blue-400' :
                    'bg-slate-700 text-slate-300'
                  }`}>
                    {audit.status}
                  </span>
                </div>
                <p className="text-sm text-slate-400">{audit.program}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowEmailModal(true)}
                className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 flex items-center gap-2"
              >
                <Mail className="h-4 w-4" />
                Email
              </button>
              {/* S4: Export PDF = print-to-PDF (browser print dialog, print styles below). */}
              <button onClick={() => window.print()} className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 flex items-center gap-2">
                <Download className="h-4 w-4" />
                Export PDF
              </button>
              {audit.status !== 'COMPLETED' && audit.status !== 'CLOSED' && (
                <button
                  onClick={() => navigate(`/safety-audits/${id}/edit`)}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-2"
                >
                  <Edit className="h-4 w-4" />
                  Edit
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="print-area container mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Score Card */}
            {audit.scorePercentage != null && (
              <div className={`rounded-xl p-6 border ${statusStyle?.border || 'border-slate-700'} ${statusStyle?.bg || 'bg-slate-800/50'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`p-4 rounded-full ${statusStyle?.bg || 'bg-slate-700'}`}>
                      {audit.passFailStatus === 'Pass' ? (
                        <Shield className="h-8 w-8 text-green-400" />
                      ) : audit.passFailStatus === 'Conditional Pass' ? (
                        <AlertCircle className="h-8 w-8 text-yellow-400" />
                      ) : (
                        <AlertTriangle className="h-8 w-8 text-red-400" />
                      )}
                    </div>
                    <div>
                      <p className={`text-3xl font-bold ${statusStyle?.text || 'text-white'}`}>
                        {statusLabel || audit.passFailStatus}
                      </p>
                      <p className="text-slate-400">
                        {audit.earnedPoints} of {audit.totalPoints} points earned
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`text-5xl font-bold ${getScoreColor(Number(audit.scorePercentage))}`}>
                      {Number(audit.scorePercentage).toFixed(0)}%
                    </p>
                    <p className="text-slate-400">Overall Score</p>
                  </div>
                </div>
              </div>
            )}

            {/* Findings */}
            {audit.findings && audit.findings.length > 0 && (
              <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
                <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-400" />
                  Findings ({audit.findings.length})
                </h2>
                <div className="space-y-4">
                  {audit.findings.map(finding => {
                    const sevStyle = SEVERITY_STYLES[finding.severity] || SEVERITY_STYLES.MINOR;
                    return (
                      <div key={finding.id} className={`p-4 rounded-lg border ${sevStyle.bg} border-slate-600`}>
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm text-slate-400">{finding.findingNumber}</span>
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${sevStyle.bg} ${sevStyle.text}`}>
                              {finding.severity}
                            </span>
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              finding.status === 'CLOSED' ? 'bg-green-900/30 text-green-400' :
                              finding.status === 'IN_PROGRESS' ? 'bg-blue-900/30 text-blue-400' :
                              'bg-slate-700 text-slate-300'
                            }`}>
                              {finding.status}
                            </span>
                            {/* S4: safety roles progress a finding OPEN → IN_PROGRESS → CLOSED (PUT /findings/:id was dead UI). */}
                            {canManageFindings && (
                              <select
                                aria-label={`Finding status for ${finding.findingNumber}`}
                                value={finding.status}
                                disabled={findingStatusSaving === finding.id}
                                onChange={(e) => void updateFindingStatus(finding.id, e.target.value)}
                                className="ml-auto px-2 py-1 rounded bg-slate-700 border border-slate-600 text-xs text-white"
                              >
                                {['OPEN', 'IN_PROGRESS', 'PENDING_VERIFICATION', 'CLOSED', 'WAIVED'].map((st) => <option key={st} value={st}>{st}</option>)}
                              </select>
                            )}
                          </div>
                        </div>
                        <h3 className="font-semibold text-white mb-1">{finding.title}</h3>
                        <p className="text-sm text-slate-300 mb-2">{finding.description}</p>
                        <p className="text-xs text-slate-500">Element: {finding.elementName}</p>
                        {finding.correctiveAction && (
                          <div className="mt-3 p-2 bg-slate-700/50 rounded text-sm">
                            <span className="text-slate-400">Corrective Action:</span>
                            <span className="text-white ml-2">{finding.correctiveAction}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Checklist Responses */}
            <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
              <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <ClipboardCheck className="h-5 w-5 text-emerald-400" />
                Checklist Responses
              </h2>
              
              {audit.template?.elements?.map((element: any) => {
                const elementResponses = audit.responses?.[element.id] || {};
                const isExpanded = expandedElements.has(element.id);
                const isNA = elementResponses.applicable === false;
                
                // Count responses for this element
                const questions = element.questions || [];
                let yesCount = 0;
                let noCount = 0;
                let naCount = 0;
                
                questions.forEach((q: any) => {
                  const resp = elementResponses.questions?.[q.id]?.response;
                  if (resp === 'Yes' || resp === true) yesCount++;
                  else if (resp === 'No' || resp === false) noCount++;
                  else if (resp === 'N/A') naCount++;
                });

                return (
                  <div key={element.id} className="mb-4 last:mb-0">
                    <button
                      onClick={() => toggleElement(element.id)}
                      className={`w-full p-4 rounded-lg flex items-center justify-between transition ${
                        isNA ? 'bg-amber-900/20 border border-amber-700/50' : 'bg-slate-700/50 hover:bg-slate-700'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="font-semibold text-white">{element.name}</span>
                        {isNA && (
                          <span className="px-2 py-0.5 bg-amber-900/50 text-amber-400 rounded text-xs">N/A</span>
                        )}
                      </div>
                      <div className="flex items-center gap-4">
                        {!isNA && (
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-green-400">{yesCount} ✓</span>
                            {noCount > 0 && <span className="text-red-400">{noCount} ✗</span>}
                            {naCount > 0 && <span className="text-amber-400">{naCount} N/A</span>}
                          </div>
                        )}
                        {isExpanded ? <ChevronUp className="h-5 w-5 text-slate-400" /> : <ChevronDown className="h-5 w-5 text-slate-400" />}
                      </div>
                    </button>
                    
                    {isExpanded && !isNA && (
                      <div className="mt-2 space-y-2 pl-4">
                        {questions.map((question: any) => {
                          const qResp = elementResponses.questions?.[question.id] || {};
                          return (
                            <div key={question.id} className="p-3 bg-slate-700/30 rounded-lg">
                              <div className="flex items-start justify-between gap-4">
                                <div className="flex-1">
                                  <p className="text-sm text-slate-300">{question.text}</p>
                                  <div className="flex gap-2 flex-wrap">{(qResp.photos || []).map((url: string) => <AuditPhoto key={url} url={url} />)}</div>
                                  {qResp.comment && (
                                    <p className="mt-1 text-xs text-slate-500 flex items-center gap-1">
                                      <MessageSquare className="h-3 w-3" />
                                      {qResp.comment}
                                    </p>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  {getResponseIcon(qResp.response)}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Audit Info */}
            <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
              <h3 className="font-bold text-white mb-4">Audit Information</h3>
              <div className="space-y-4 text-sm">
                <div className="flex items-start gap-3">
                  <Calendar className="h-4 w-4 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-slate-400">Date</p>
                    <p className="text-white">{new Date(audit.startDate).toLocaleDateString(undefined, { timeZone: 'UTC' })}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <User className="h-4 w-4 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-slate-400">Auditor</p>
                    <p className="text-white">{audit.auditorName}</p>
                  </div>
                </div>
                {audit.constructionSupervisor && (
                  <div className="flex items-start gap-3">
                    <User className="h-4 w-4 text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-slate-400">Supervisor</p>
                      <p className="text-white">{audit.constructionSupervisor}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Site Info */}
            <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
              <h3 className="font-bold text-white mb-4">Site Information</h3>
              <div className="space-y-4 text-sm">
                {audit.siteName && (
                  <div className="flex items-start gap-3">
                    <MapPin className="h-4 w-4 text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-slate-400">Site</p>
                      <p className="text-white">{audit.siteName}</p>
                      {audit.siteId && <p className="text-slate-500 text-xs">ID: {audit.siteId}</p>}
                    </div>
                  </div>
                )}
                {audit.siteAddress && (
                  <div className="flex items-start gap-3">
                    <Building2 className="h-4 w-4 text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-slate-400">Address</p>
                      <p className="text-white">{audit.siteAddress}</p>
                      <p className="text-white">{audit.city}, {audit.state}</p>
                    </div>
                  </div>
                )}
                {audit.carrierName && (
                  <div className="flex items-start gap-3">
                    <Radio className="h-4 w-4 text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-slate-400">Carrier</p>
                      <p className="text-white">{audit.carrierName}</p>
                    </div>
                  </div>
                )}
                {audit.contractorName && (
                  <div className="flex items-start gap-3">
                    <Building2 className="h-4 w-4 text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-slate-400">Contractor</p>
                      <p className="text-white">{audit.contractorName}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Signatures */}
            {(audit.auditorSignature || audit.supervisorSignature) && (
              <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
                <h3 className="font-bold text-white mb-4">Signatures</h3>
                <div className="space-y-4">
                  {audit.auditorSignature && (
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Auditor Signature</p>
                      <p className="text-white font-signature text-xl border-b border-slate-600 pb-2">
                        {audit.auditorSignature}
                      </p>
                    </div>
                  )}
                  {audit.supervisorSignature && (
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Supervisor Signature</p>
                      <p className="text-white font-signature text-xl border-b border-slate-600 pb-2">
                        {audit.supervisorSignature}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Comments */}
            {audit.overallComments && (
              <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
                <h3 className="font-bold text-white mb-4">Overall Comments</h3>
                <p className="text-slate-300 text-sm whitespace-pre-wrap">{audit.overallComments}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold text-white mb-4">Email Audit Report</h3>
            <p className="text-slate-400 mb-4">
              Send the audit summary and findings to the addresses below. Confirm the recipients before sending.
            </p>
            <label className="block text-white mb-3">Recipient email addresses<input value={recipients} onChange={e => setRecipients(e.target.value)} className="w-full p-2 text-gray-900 rounded" placeholder="name@company.com" /></label>
            {emailStatus && <p role="status" className="text-white mb-3">{emailStatus}</p>}
            <button disabled={emailSending || !recipients.trim()} onClick={sendReport} className="p-2 bg-blue-600 text-white rounded">{emailSending ? 'Sending…' : 'Send report'}</button>
            <div className="flex justify-end">
              <button
                onClick={() => setShowEmailModal(false)}
                className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

